/**
 * Statistique — le DÉTAIL de l'activité.
 *
 * Pendant de `/tableau-de-bord` : la vue d'ensemble y donne les chiffres clés
 * d'un coup d'œil, cette page-ci creuse. Répartitions par statut, rythme
 * hebdomadaire, tableau client complet (tri, recherche, pagination) et
 * historique des commandes — chaque section a son propre filtre de période.
 *
 * Aucun calcul ici : tout vient de `useProDashboard` → `@erp/domain/pro`.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  LayoutDashboard,
  Search,
  Users,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import {
  COMMANDE_STATUSES,
  DEVIS_STATUSES,
  computeMonthlySeries,
  computeStatusBreakdown,
  computeWeekdayDistribution,
  filterByRange,
  resolveStatsRange,
  type ProClientStatRow,
  type ProStatsRange,
} from '@erp/domain/pro';
import { PeriodPicker } from '@/components/common/PeriodPicker';
import { Pagination } from '@/components/common/Pagination';
import { CommandeStatusBadge } from '@/components/common/StatusBadge';
import { useDataService } from '@/lib/service-context';
import { formatDate, formatTND } from '@/lib/format';
import { useProDashboard } from '@/hooks/use-pro-dashboard';
import {
  ChartSkeleton,
  ClientStatCard,
  DEVIS_STATUS_COLORS,
  DonutCard,
  LegendDot,
  MonthTooltip,
  ORDER_STATUS_COLORS,
  ShareBar,
  Th,
  clientLabel,
  fmtInt,
  makeMonthLabel,
  makeWeekdayLabels,
} from './stats/stats-ui';

type ClientSortKey = 'name' | 'orders' | 'revenue' | 'collected' | 'commission';
type SortDir = 'asc' | 'desc';

export function StatistiquePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const ds = useDataService();

  // Chaque section garde SON horizon : on compare des choses différentes.
  const [range, setRange] = useState<ProStatsRange>('this_year');
  const [chartMonths, setChartMonths] = useState<'6' | '12' | 'all'>('12');

  const [clientSearch, setClientSearch] = useState('');
  const [clientSort, setClientSort] = useState<ClientSortKey>('revenue');
  const [clientDir, setClientDir] = useState<SortDir>('desc');
  const [clientPage, setClientPage] = useState(1);
  const [ordersPage, setOrdersPage] = useState(1);
  const pageSize = 10;

  const dashboard = useProDashboard(range);
  const { clients } = dashboard;

  const clientsQuery = useQuery({
    queryKey: ['pro-clients'],
    queryFn: () => ds.proClients.list(),
    staleTime: 60_000,
  });

  const clientNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of clientsQuery.data ?? []) {
      map.set(c.id, [c.prenom, c.nom].filter(Boolean).join(' ').trim() || c.nom);
    }
    return map;
  }, [clientsQuery.data]);

  const rangeOptions = useMemo(
    () => [
      { key: 'this_week' as const, label: t('pro.statistique.range.this_week', 'Cette semaine') },
      { key: 'this_month' as const, label: t('pro.statistique.range.this_month', 'Ce mois') },
      { key: 'last_month' as const, label: t('pro.statistique.range.last_month', 'Mois dernier') },
      { key: 'last_3m' as const, label: t('pro.statistique.range.last_3m', '3 derniers mois') },
      { key: 'last_12m' as const, label: t('pro.statistique.range.last_12m', '12 derniers mois') },
      { key: 'this_year' as const, label: t('pro.statistique.range.this_year', 'Cette année') },
      { key: 'all' as const, label: t('pro.statistique.range.all', 'Depuis toujours') },
    ],
    [t],
  );

  const chartOptions = useMemo(
    () => [
      { key: '6' as const, label: t('pro.statistique.range.last_6m', '6 derniers mois') },
      { key: '12' as const, label: t('pro.statistique.range.last_12m', '12 derniers mois') },
      { key: 'all' as const, label: t('pro.statistique.range.all', 'Depuis toujours') },
    ],
    [t],
  );

  const bounds = useMemo(() => resolveStatsRange(range, new Date()), [range]);
  const periodCommandes = useMemo(
    () => filterByRange(dashboard.commandes, bounds),
    [dashboard.commandes, bounds],
  );
  const periodDevis = useMemo(() => filterByRange(dashboard.devis, bounds), [dashboard.devis, bounds]);

  const monthly = useMemo(
    () =>
      computeMonthlySeries(dashboard.commandes, dashboard.walletMovements, {
        months: chartMonths === 'all' ? null : Number(chartMonths),
      }),
    [dashboard.commandes, dashboard.walletMovements, chartMonths],
  );

  const devisSlices = useMemo(() => computeStatusBreakdown(periodDevis, DEVIS_STATUSES), [periodDevis]);
  const orderSlices = useMemo(() => computeStatusBreakdown(periodCommandes, COMMANDE_STATUSES), [periodCommandes]);
  const weekdays = useMemo(() => computeWeekdayDistribution(periodCommandes), [periodCommandes]);
  const maxWeekday = Math.max(...weekdays.map((p) => p.orders));

  const sortedClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    const rows = q
      ? clients.filter((row) => clientLabel(row, clientNames, t).toLowerCase().includes(q))
      : clients;
    const dir = clientDir === 'asc' ? 1 : -1;
    const value = (row: ProClientStatRow): string | number => {
      switch (clientSort) {
        case 'name':
          return clientLabel(row, clientNames, t).toLowerCase();
        case 'orders':
          return row.ordersCount;
        case 'collected':
          return row.collected;
        case 'commission':
          return row.commission;
        case 'revenue':
        default:
          return row.revenueTtc;
      }
    };
    return [...rows].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      return va < vb ? -dir : va > vb ? dir : 0;
    });
  }, [clients, clientSearch, clientNames, clientSort, clientDir, t]);

  const pagedClients = sortedClients.slice((clientPage - 1) * pageSize, clientPage * pageSize);
  const pagedOrders = periodCommandes.slice((ordersPage - 1) * pageSize, ordersPage * pageSize);

  const monthLabel = useMemo(() => makeMonthLabel(i18n.language), [i18n.language]);
  const weekdayLabels = useMemo(() => makeWeekdayLabels(i18n.language), [i18n.language]);

  const toggleSort = (key: ClientSortKey) => {
    if (clientSort === key) setClientDir(clientDir === 'asc' ? 'desc' : 'asc');
    else {
      setClientSort(key);
      setClientDir(key === 'name' ? 'asc' : 'desc');
    }
    setClientPage(1);
  };

  if (dashboard.isError) {
    return (
      <div className="pro-card p-10 text-center">
        <p className="text-[14px]" style={{ color: 'var(--pro-danger)' }}>
          {t('pro.statistique.load_error', 'Impossible de charger vos statistiques.')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Renvoi vers la vue d'ensemble + filtre global des sections « période ». */}
      <div className="pro-card px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => navigate('/tableau-de-bord')}
          className="inline-flex items-center gap-2 text-[13px] font-medium"
          style={{ color: 'var(--pro-primary)', fontFamily: 'Figtree' }}
        >
          <LayoutDashboard className="w-4 h-4" />
          {t('pro.statistique.back_to_dashboard', "Retour à la vue d'ensemble")}
        </button>
        <div className="flex items-center gap-2">
          <span className="text-[12px]" style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}>
            {t('pro.statistique.period_label', 'Période')}
          </span>
          <PeriodPicker value={range} options={rangeOptions} onChange={setRange} />
        </div>
      </div>

      {/* ---------- Évolution des commissions ---------- */}
      <div className="pro-card p-4">
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div className="flex flex-col gap-1">
            <span className="text-[16px] font-semibold" style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}>
              {t('pro.statistique.total_commissions', 'Évolution des commissions')}
            </span>
            <span className="text-[12px]" style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}>
              {t('pro.statistique.commissions_legend', 'Validée + en attente, mois par mois')}
            </span>
          </div>
          <PeriodPicker value={chartMonths} options={chartOptions} onChange={setChartMonths} />
        </div>
        <div className="h-[260px]">
          {dashboard.isLoading ? (
            <ChartSkeleton />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthly} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="statCommissionValidated" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF6B1E" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#FF6B1E" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="statCommissionPending" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F6A723" stopOpacity={0.22} />
                    <stop offset="100%" stopColor="#F6A723" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#EBEBEB" vertical={false} />
                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#858585', fontSize: 12 }}
                  tickFormatter={monthLabel}
                  minTickGap={12}
                />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#858585', fontSize: 12 }} width={48} />
                <Tooltip
                  cursor={{ stroke: '#FFCDA8', strokeWidth: 1, strokeDasharray: '4 3' }}
                  content={
                    <MonthTooltip
                      monthLabel={monthLabel}
                      validatedLabel={t('pro.statistique.wallet_validated', 'Commission validée')}
                      pendingLabel={t('pro.statistique.wallet_pending', 'Commission en attente')}
                      ordersLabel={t('pro.statistique.orders_label', 'Commandes')}
                    />
                  }
                />
                <Area isAnimationActive={false}
                  type="monotone"
                  dataKey="commissionValidated"
                  stackId="commission"
                  stroke="#FF6B1E"
                  strokeWidth={2}
                  fill="url(#statCommissionValidated)"
                  dot={false}
                  activeDot={{ r: 5, fill: '#FF6B1E', stroke: '#fff', strokeWidth: 2 }}
                />
                <Area isAnimationActive={false}
                  type="monotone"
                  dataKey="commissionPending"
                  stackId="commission"
                  stroke="#F6A723"
                  strokeWidth={2}
                  strokeDasharray="4 3"
                  fill="url(#statCommissionPending)"
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-5 mt-5">
          <LegendDot color="#FF6B1E" label={t('pro.statistique.wallet_validated', 'Commission validée')} />
          <LegendDot color="#F6A723" label={t('pro.statistique.wallet_pending', 'Commission en attente')} />
        </div>
      </div>

      {/* ---------- Répartitions par statut ---------- */}
      <div className="grid lg:grid-cols-2 gap-4">
        <DonutCard
          title={t('pro.statistique.devis_breakdown', 'Devis par statut')}
          slices={devisSlices}
          colors={DEVIS_STATUS_COLORS}
          labelFor={(status) => t(`pro.devis.status.${status}`, status)}
          emptyLabel={t('pro.statistique.empty', 'Pas encore de données')}
        />
        <DonutCard
          title={t('pro.statistique.orders_breakdown', 'Commandes par statut')}
          slices={orderSlices}
          colors={ORDER_STATUS_COLORS}
          labelFor={(status) => t(`pro.commande.status.${status}`, status)}
          emptyLabel={t('pro.statistique.empty', 'Pas encore de données')}
        />
      </div>

      {/* ---------- Rythme hebdomadaire ---------- */}
      <div className="pro-card p-4">
        <h2 className="text-[16px] font-semibold mb-1" style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}>
          {t('pro.statistique.orders_overview', 'Commandes par jour de la semaine')}
        </h2>
        <p className="text-[12px] mb-4" style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}>
          {t('pro.statistique.weekday_hint', 'Vos jours les plus actifs sur la période')}
        </p>
        <div className="h-[240px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={weekdays.map((p) => ({ ...p, label: weekdayLabels[p.weekday] ?? '' }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EBEBEB" vertical={false} />
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#858585', fontSize: 12 }} />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#858585', fontSize: 12 }}
                allowDecimals={false}
                width={32}
              />
              <Tooltip
                cursor={{ fill: 'rgba(255,107,30,0.05)' }}
                contentStyle={{ background: '#333', border: 'none', borderRadius: 6, fontSize: 12, color: '#fff' }}
                labelStyle={{ color: '#DEDEDE', fontSize: 10 }}
                formatter={(value, key) =>
                  key === 'revenueTtc'
                    ? [formatTND(Number(value)), t('pro.statistique.col_revenue', 'CA TTC')]
                    : [fmtInt(Number(value)), t('pro.statistique.orders_label', 'Commandes')]
                }
              />
              {/* Sans animation : recharts ne peint la géométrie qu'au fil des
                  frames, donc un onglet au rAF bridé affiche des barres vides. */}
              <Bar dataKey="orders" radius={[8, 8, 0, 0]} isAnimationActive={false}>
                {weekdays.map((point) => (
                  <Cell
                    key={point.weekday}
                    fill={point.orders > 0 && point.orders === maxWeekday ? '#FF6B1E' : '#FFEEE0'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ---------- Tableau client complet ---------- */}
      <div className="pro-card p-4 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-[16px] font-semibold" style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}>
              {t('pro.statistique.clients_title', 'Statistiques par client')}
            </h2>
            <p className="text-[12px] mt-0.5" style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}>
              {t('pro.statistique.clients_subtitle', 'Ce que chaque client vous rapporte sur la période')}
            </p>
          </div>
          <div className="relative flex-1 sm:flex-none min-w-[180px] sm:max-w-[260px]">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
              style={{ color: 'var(--pro-text-grey)' }}
            />
            <input
              type="search"
              value={clientSearch}
              onChange={(e) => {
                setClientSearch(e.target.value);
                setClientPage(1);
              }}
              placeholder={t('pro.statistique.clients_search', 'Rechercher un client')}
              className="w-full pl-10 pr-3 h-9 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-orange-300"
              style={{ background: 'var(--pro-grey-subtle)', color: 'var(--pro-text-dark)' }}
            />
          </div>
        </div>

        {dashboard.isLoading ? (
          <div className="py-12 text-center text-[14px]" style={{ color: 'var(--pro-text-grey)' }}>
            {t('pro.common.loading', 'Chargement…')}
          </div>
        ) : sortedClients.length === 0 ? (
          <div className="py-12 text-center">
            <Users className="w-9 h-9 mx-auto mb-2" style={{ color: 'var(--pro-text-grey-light)' }} />
            <p className="text-[14px]" style={{ color: 'var(--pro-text-grey)' }}>
              {t('pro.statistique.clients_empty', 'Aucun client sur cette période')}
            </p>
          </div>
        ) : (
          <>
            <div className="md:hidden space-y-3">
              {pagedClients.map((row) => (
                <ClientStatCard
                  key={row.clientId ?? 'unassigned'}
                  row={row}
                  name={clientLabel(row, clientNames, t)}
                  onOpen={row.clientId ? () => navigate(`/clients/${encodeURIComponent(row.clientId!)}`) : undefined}
                />
              ))}
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr>
                    <SortTh label={t('pro.statistique.col_client', 'Client')} sortKey="name" current={clientSort} dir={clientDir} onSort={toggleSort} />
                    <SortTh label={t('pro.statistique.col_orders', 'Commandes')} sortKey="orders" current={clientSort} dir={clientDir} onSort={toggleSort} align="right" />
                    <Th align="right">{t('pro.statistique.col_quotes', 'Devis')}</Th>
                    <SortTh label={t('pro.statistique.col_revenue', 'CA TTC')} sortKey="revenue" current={clientSort} dir={clientDir} onSort={toggleSort} align="right" />
                    <SortTh label={t('pro.statistique.col_collected', 'Encaissé')} sortKey="collected" current={clientSort} dir={clientDir} onSort={toggleSort} align="right" />
                    <SortTh label={t('pro.statistique.col_commission', 'Commission')} sortKey="commission" current={clientSort} dir={clientDir} onSort={toggleSort} align="right" />
                    <Th align="right">{t('pro.statistique.col_share', 'Part du CA')}</Th>
                    <Th>{t('pro.statistique.col_last_activity', 'Dernière activité')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {pagedClients.map((row) => (
                    <tr
                      key={row.clientId ?? 'unassigned'}
                      style={{ borderBottom: '1px solid var(--pro-grey-subtle)' }}
                      className={row.clientId ? 'hover:bg-orange-50/40 cursor-pointer' : ''}
                      onClick={row.clientId ? () => navigate(`/clients/${encodeURIComponent(row.clientId!)}`) : undefined}
                    >
                      <td className="py-3 px-3">
                        <span className="font-medium" style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}>
                          {clientLabel(row, clientNames, t)}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-right" style={{ color: 'var(--pro-text-dark)' }}>
                        {fmtInt(row.ordersCount)}
                      </td>
                      <td className="py-3 px-3 text-right" style={{ color: 'var(--pro-text-grey)' }}>
                        {fmtInt(row.quotesCount)}
                      </td>
                      <td className="py-3 px-3 text-right font-medium" style={{ color: 'var(--pro-text-dark)' }}>
                        {formatTND(row.revenueTtc)}
                      </td>
                      <td className="py-3 px-3 text-right" style={{ color: 'var(--pro-text-dark)' }}>
                        {formatTND(row.collected)}
                      </td>
                      <td className="py-3 px-3 text-right font-semibold" style={{ color: 'var(--pro-primary)' }}>
                        {formatTND(row.commission)}
                      </td>
                      <td className="py-3 px-3">
                        <ShareBar pct={row.revenueShare} />
                      </td>
                      <td className="py-3 px-3" style={{ color: 'var(--pro-text-grey)' }}>
                        {row.lastActivityDate ? formatDate(row.lastActivityDate) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {sortedClients.length > pageSize && (
              <Pagination page={clientPage} pageSize={pageSize} total={sortedClients.length} onPageChange={setClientPage} />
            )}
          </>
        )}
      </div>

      {/* ---------- Historique des commandes ---------- */}
      <div className="pro-card p-4 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-[16px] font-semibold" style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}>
            {t('pro.statistique.orders_history', 'Historique des commandes')}
          </h2>
          <button
            type="button"
            onClick={() => navigate('/ordres')}
            className="text-[12.5px] font-medium"
            style={{ color: 'var(--pro-primary)', fontFamily: 'Figtree' }}
          >
            {t('pro.statistique.view_all', 'Voir toutes les commandes')}
          </button>
        </div>

        {periodCommandes.length === 0 ? (
          <p className="text-[14px] py-8 text-center" style={{ color: 'var(--pro-text-grey)' }}>
            {t('pro.statistique.no_orders', 'Aucune commande sur cette période')}
          </p>
        ) : (
          <>
            <div className="md:hidden space-y-3">
              {pagedOrders.map((order) => (
                <button
                  key={order.code}
                  type="button"
                  onClick={() => navigate(`/ordres/${encodeURIComponent(order.code)}`)}
                  className="w-full text-left py-4 space-y-3 active:opacity-70"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[12px] font-medium" style={{ color: 'var(--pro-text-grey)' }}>
                      {order.code}
                    </span>
                    <CommandeStatusBadge status={order.status} />
                  </div>
                  <div className="text-[13px]" style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}>
                    {order.proClientId
                      ? clientNames.get(order.proClientId) ?? t('pro.statistique.unknown_client', 'Client supprimé')
                      : t('pro.statistique.no_client', 'Sans client')}
                  </div>
                  <div
                    className="flex items-center justify-between gap-2"
                    
                  >
                    <div>
                      <div className="text-[11px]" style={{ color: 'var(--pro-text-grey)' }}>
                        {t('pro.statistique.col_revenue', 'CA TTC')}
                      </div>
                      <div className="text-[14px] font-semibold" style={{ color: 'var(--pro-text-dark)' }}>
                        {formatTND(order.pttc)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[11px]" style={{ color: 'var(--pro-text-grey)' }}>
                        {t('pro.statistique.commission_label', 'Commission')}
                      </div>
                      <div className="text-[14px] font-semibold" style={{ color: 'var(--pro-primary)' }}>
                        {formatTND(order.totalCommission)}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr>
                    <Th>{t('pro.statistique.col_order', 'N° commande')}</Th>
                    <Th>{t('pro.statistique.col_date', 'Date')}</Th>
                    <Th>{t('pro.statistique.col_client', 'Client')}</Th>
                    <Th align="right">{t('pro.statistique.col_revenue', 'CA TTC')}</Th>
                    <Th align="right">{t('pro.statistique.col_collected', 'Encaissé')}</Th>
                    <Th align="right">{t('pro.statistique.col_commission', 'Commission')}</Th>
                    <Th>{t('pro.statistique.col_status', 'Statut')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {pagedOrders.map((order) => (
                    <tr
                      key={order.code}
                      className="cursor-pointer hover:bg-orange-50/40"
                      style={{ borderBottom: '1px solid var(--pro-grey-subtle)' }}
                      onClick={() => navigate(`/ordres/${encodeURIComponent(order.code)}`)}
                    >
                      <td className="py-3 px-3 font-medium" style={{ color: 'var(--pro-text-dark)' }}>
                        {order.code}
                      </td>
                      <td className="py-3 px-3" style={{ color: 'var(--pro-text-grey)' }}>
                        {formatDate(order.date)}
                      </td>
                      <td className="py-3 px-3" style={{ color: 'var(--pro-text-dark)' }}>
                        {order.proClientId
                          ? clientNames.get(order.proClientId) ?? t('pro.statistique.unknown_client', 'Client supprimé')
                          : t('pro.statistique.no_client', 'Sans client')}
                      </td>
                      <td className="py-3 px-3 text-right" style={{ color: 'var(--pro-text-dark)' }}>
                        {formatTND(order.pttc)}
                      </td>
                      <td className="py-3 px-3 text-right" style={{ color: 'var(--pro-text-dark)' }}>
                        {formatTND(order.totalAPayer)}
                      </td>
                      <td className="py-3 px-3 text-right font-semibold" style={{ color: 'var(--pro-primary)' }}>
                        {formatTND(order.totalCommission)}
                      </td>
                      <td className="py-3 px-3">
                        <CommandeStatusBadge status={order.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {periodCommandes.length > pageSize && (
              <Pagination page={ordersPage} pageSize={pageSize} total={periodCommandes.length} onPageChange={setOrdersPage} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SortTh({
  label,
  sortKey,
  current,
  dir,
  onSort,
  align = 'left',
}: {
  label: string;
  sortKey: ClientSortKey;
  current: ClientSortKey;
  dir: SortDir;
  onSort: (key: ClientSortKey) => void;
  align?: 'left' | 'right';
}) {
  const active = current === sortKey;
  const Icon = !active ? ChevronsUpDown : dir === 'asc' ? ChevronUp : ChevronDown;
  return (
    <th
      className={`py-3 px-3 text-[12px] font-medium select-none whitespace-nowrap ${align === 'right' ? 'text-right' : 'text-left'}`}
      style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1"
        style={{ color: active ? 'var(--pro-text-dark)' : 'var(--pro-text-grey)' }}
      >
        {label}
        <Icon className="w-3 h-3" />
      </button>
    </th>
  );
}
